import {
  Stack,
  useColorMode,
  IconButton,
  useColorModeValue,
  useBreakpointValue,
  Center,
  Tag,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Text,
} from "@chakra-ui/react";
import {
  useCustomTheme,
  useEntitlementKey,
  useSidebarNavigation,
  useUserPreference,
  useLatestCollectionWorkflowId,
  useLatestDueDiligenceProject,
  useTemporaryProjects,
} from "hooks";
import type { FunctionComponent } from "react";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { NotificationsMenu } from "./NotificationsMenu";
import { ProfileSettingsMenu } from "./ProfileSettingsMenu";
import { MoonIcon, SunIcon } from "@chakra-ui/icons";
import { updateTypedUserPreference } from "state/userPreference/operations";
import { useDispatch } from "react-redux";
import { getEnvironment } from "screens/common/app";
import { DEFAULT_SIDEBAR_WIDTH } from "./SidebarNavigation";
import { HEADER_HEIGHT, HEADER_ICON_HEIGHT } from "../Landing";
import { ProgressIndicator } from "screens/common/components/ProgressIndicator";
import { useWorkflowCompletionDateOrNow, useWorkflowKey } from "hooks/useWorkflows";
import { ProjectHeaderImage } from "../tabs/project/projectLandingTileLayouts/components/ProjectHeaderImage";
import { WorkflowProgressStepperContainer } from "./WorkflowProgressStepper";
import { useNavigate } from "react-router-dom";
import type { WorkflowTaskStatus } from "types/workflows/workflow";
import type { Collection } from "types/collection";

interface Props {
  initialText?: string;
}

const InteractionBarComponent: FunctionComponent<Props> = ({ initialText = "" }) => {
  const { isEmbeddedApp } = useCustomTheme();
  const uiColorMode = useUserPreference("ui_color_mode") as string;
  const { colorMode, setColorMode } = useColorMode();
  const hasThemes = useEntitlementKey("ui_themes");
  const iconColor = useColorModeValue("gray.400", "gray.500");
  const dispatch = useDispatch();
  const showEnvBadgePreference = useUserPreference("ui_show_env_badge");
  const injectDebugEntityPreference = useUserPreference("ui_inject_debug_entity");
  const injectTestEntityPreference = useUserPreference("ui_inject_test_entity");
  const debugBgColor = useColorModeValue("#2c8741", "gray.700");
  const testBgColor = useColorModeValue("#6694a3", "gray.700");
  const badgeFontColor = useColorModeValue("gray.50", "gray.300");
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const sidebarWidth = (useUserPreference("ui_sidebar_width") as number) || (DEFAULT_SIDEBAR_WIDTH as number);
  const { currentSidebarType } = useSidebarNavigation();
  const latestDueDiligenceProject = useLatestDueDiligenceProject();
  const navigate = useNavigate();
  const [latestProject, setLatestProject] = useState<Collection | undefined>(undefined);

  const currentWorkflowId = useLatestCollectionWorkflowId(latestProject?.id);
  const currentWorkflowCompletionDate = useWorkflowCompletionDateOrNow(currentWorkflowId);
  const workflowStatus = useWorkflowKey(currentWorkflowId, "status");
  const textColor = useColorModeValue("primary.darkGray", "gray.300");
  const tileBgColor = useColorModeValue("white", "#161B25");
  const { temporaryProjects, tickerToLogoMap } = useTemporaryProjects();

  useEffect(() => {
    if (temporaryProjects.length > 0) {
      setLatestProject(undefined);
      return;
    }
    if (latestDueDiligenceProject && latestDueDiligenceProject.name !== "New Project") {
      setLatestProject(latestDueDiligenceProject);
    } else {
      setLatestProject(undefined);
    }
  }, [temporaryProjects, latestDueDiligenceProject]);

  const workflowStatusToShow = useMemo(
    () => ["failed", "in_progress", "incomplete", "error", "clarification_needed", "failed_checkstop"] as WorkflowTaskStatus[],
    []
  );
  const workflowStatusFailed = useMemo(
    () => ["failed", "incomplete", "error", "clarification_needed", "failed_checkstop"] as WorkflowTaskStatus[],
    []
  );

  const hasValidWorkflow = !!latestProject && !!currentWorkflowId;
  const completionDate = hasValidWorkflow ? currentWorkflowCompletionDate : undefined;

  const toggleColorMode = useCallback(() => {
    const newColorMode = uiColorMode === "light" ? "dark" : "light";
    dispatch(updateTypedUserPreference({ preferenceKey: "ui_color_mode", value: newColorMode }));
    setColorMode(newColorMode);
  }, [uiColorMode, dispatch, setColorMode]);

  return (
    <Stack
      position="relative"
      direction="column"
      width={`${isMobile ? "10rem" : currentSidebarType === "hidden" ? "19rem" : `${sidebarWidth}px`}`}
      minWidth={`${currentSidebarType === "hidden" ? 0 : sidebarWidth}px`}
      justifyContent="flex-end"
      p="0.5rem"
      spacing=".5rem">
      <Stack direction="row" spacing="0.5rem" justifyContent="flex-end" align="center" height={"100%"}>
        {(temporaryProjects.length > 0 || (workflowStatus && workflowStatusToShow.includes(workflowStatus))) && (
          <Popover trigger="hover" placement="bottom" gutter={-1}>
            <PopoverTrigger>
              <Center cursor="default" width="100%" height="100%">
                <Box height="1.6rem">
                  <ProgressIndicator
                    size={isMobile ? 20 : 28}
                    isAnimating={workflowStatus ? !workflowStatusFailed.includes(workflowStatus) : true}
                    onClick={() => {
                      if (latestProject && latestProject.id) {
                        navigate(`/projects/due_diligence`);
                      }
                    }}
                  />
                </Box>
              </Center>
            </PopoverTrigger>
            <PopoverContent width="19rem" p={".5rem"} boxShadow={"xl"} bgColor={tileBgColor} borderRadius={"lg"}>
              <PopoverBody
                className="ch-project-progress-popover-content"
                cursor={latestProject && latestProject.id ? "pointer" : "default"}
                onClick={() => {
                  if (latestProject && latestProject.id) {
                    navigate(`/projects/due_diligence/${latestProject.id}`);
                  }
                }}>
                <Stack spacing={"0.75rem"} className="ch-project-default-icon" width="100%">
                  <ProjectHeaderImage
                    maxHeight="1.4rem"
                    collectionId={latestProject?.id}
                    projectName={latestProject?.name !== "New Project" ? latestProject?.name : ""}
                    projectType={"due_diligence"}
                    logoOnly
                    logoUrl={tickerToLogoMap[latestProject?.ticker?.toLowerCase() || temporaryProjects[0]?.ticker?.toLowerCase()]}
                  />
                  {!workflowStatus || workflowStatus === "in_progress" ? (
                    <Stack spacing="5px" width="100%">
                      <Text fontSize={"xs"} color={textColor} lineHeight={"1.2rem"}>
                        {`Charli AI agents are working in the background analyzing your stocks and the market ${
                          latestProject && latestProject?.name !== "New Project" ? `for ${latestProject?.name}` : ""
                        }.`}
                      </Text>
                      <Text fontSize={"xs"} color={textColor} lineHeight={"1.2rem"}>
                        {latestProject
                          ? `Click to open the project and see
                      what's running.`
                          : `Your project is being generated now.`}
                      </Text>
                    </Stack>
                  ) : (
                    <Text fontSize={"xs"} color={textColor} lineHeight={"1.2rem"}>
                      {`Your project ${
                        latestProject && latestProject?.name !== "New Project" ? `for ${latestProject?.name}` : ""
                      } requires your attention. Please click here to open the project for more details.`}
                    </Text>
                  )}
                  {hasValidWorkflow && completionDate && (
                    <Center>
                      <WorkflowProgressStepperContainer collectionId={latestProject.id} hideProgressBar={false} />
                    </Center>
                  )}
                </Stack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
        {!isMobile && <NotificationsMenu />}
        {!isMobile && hasThemes && (
          <IconButton
            aria-label="Toggle color mode"
            paddingInline={0}
            color={iconColor}
            icon={
              colorMode === "light" ? (
                <MoonIcon boxSize={`${HEADER_ICON_HEIGHT - 6}px`} />
              ) : (
                <SunIcon boxSize={`${HEADER_ICON_HEIGHT - 6}px`} />
              )
            }
            onClick={toggleColorMode}
            variant="ghost"
            backgroundColor="transparent"
            _hover={{ color: "primary.default", bgColor: "transparent" }}
          />
        )}
        {!isEmbeddedApp && <ProfileSettingsMenu />}
      </Stack>
      {!isMobile && (
        <Stack
          spacing="3px"
          direction="row"
          justifyContent={"flex-end"}
          right="0.5rem"
          position="absolute"
          zIndex={9}
          top={`${HEADER_HEIGHT - 10}px`}>
          {showEnvBadgePreference && getEnvironment().label.length > 0 && (
            <Center>
              <Tag
                borderRadius={"3px"}
                px="5px"
                py="1px"
                width="100%"
                textAlign={"center"}
                fontSize="8px"
                minHeight={".8rem"}
                color={badgeFontColor}
                backgroundColor={colorMode === "dark" ? "gray.700" : getEnvironment().color}>
                {getEnvironment().label}
              </Tag>
            </Center>
          )}
          {injectDebugEntityPreference && (
            <Center>
              <Tag
                borderRadius={"3px"}
                px="5px"
                py="1px"
                width="100%"
                textAlign={"center"}
                fontSize="8px"
                minHeight={".8rem"}
                color={badgeFontColor}
                backgroundColor={debugBgColor}>
                DEBUG
              </Tag>
            </Center>
          )}
          {injectTestEntityPreference && (
            <Center>
              <Tag
                borderRadius={"3px"}
                px="5px"
                py="1px"
                width="100%"
                textAlign={"center"}
                fontSize="8px"
                minHeight={".8rem"}
                color={badgeFontColor}
                backgroundColor={testBgColor}>
                TESTING
              </Tag>
            </Center>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export const InteractionBar = memo(InteractionBarComponent);
